import { useEffect } from 'react';
import { useNavigationType, useSearchParams } from 'react-router-dom';
import Container from '@/components/base/Container';
import Section from '@/components/base/Section';
import SectionHeading from '@/components/base/SectionHeading';
import PageIntro from '@/components/base/PageIntro';
import InquiryForm from './components/InquiryForm';
import FaqList from './components/FaqList';

const guidance = [
  { icon: 'ri-community-line', title: 'Boards & owners', text: 'Use Property Partnership to ask about management services. Include the community type and any timing considerations.' },
  { icon: 'ri-user-voice-line', title: 'Residents', text: 'Use Resident Support for questions or non-urgent requests. Describe the issue and its location in shared areas.' },
  { icon: 'ri-alarm-warning-line', title: 'Emergencies', text: 'Call local emergency services first. For urgent building issues, use your community\u2019s posted after-hours number.' },
];

export default function Contact() {
  const [params] = useSearchParams();
  const initialMode = params.get('type') === 'resident' ? 'resident' : 'partnership';
  const hasType = params.has('type');
  const navType = useNavigationType();

  useEffect(() => {
    // Footer/CTA links with ?type= bring the form into view (no smooth scrolling).
    if (!hasType || navType === 'POP') return undefined;
    const frame = requestAnimationFrame(() => {
      document.getElementById('contact-inquiry')?.scrollIntoView({ block: 'start' });
    });
    return () => cancelAnimationFrame(frame);
  }, [initialMode, hasType, navType]);

  return (
    <>
      <PageIntro
        eyebrow="Contact"
        title="Two ways to reach us."
        lead="Board members and property owners can request a management proposal. Residents can send a support inquiry. Choose the option that fits, and the form adjusts to ask only what is needed."
      />

      <Section labelledBy="form-heading" divider>
        <Container>
          <div className="grid grid-cols-1 gap-10 lg:grid-cols-12 lg:gap-6">
            <div className="lg:col-span-4">
              <h2 id="form-heading" className="font-heading text-[28px] font-semibold leading-[1.12] text-foreground-950 md:text-[34px]">Send an inquiry</h2>
              <p className="mt-4 max-w-[360px] text-[16px] leading-[1.6] text-foreground-700">
                All fields are required unless marked optional. We only ask for information needed to respond.
              </p>
            </div>
            <div className="lg:col-span-8">
              <InquiryForm key={initialMode} initialMode={initialMode} />
            </div>
          </div>
        </Container>
      </Section>

      <Section labelledBy="guidance-heading">
        <Container>
          <SectionHeading
            id="guidance-heading"
            eyebrow="Guidance"
            title="Which option should I choose?"
            lead="Choose the option that best matches your question."
          />
          <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
            {guidance.map((g) => (
              <article key={g.title} className="flex h-full flex-col rounded-[12px] border border-background-200 p-6">
                <span className="flex h-11 w-11 items-center justify-center rounded-full bg-primary-50 text-[20px] text-accent-500">
                  <i className={g.icon} aria-hidden="true"></i>
                </span>
                <h3 className="mt-6 font-heading text-[20px] font-semibold text-foreground-950 md:text-[22px]">{g.title}</h3>
                <p className="mt-3 text-[16px] leading-[1.6] text-foreground-700">{g.text}</p>
              </article>
            ))}
          </div>
        </Container>
      </Section>

      <Section labelledBy="faq-heading">
        <Container>
          <div className="grid grid-cols-1 gap-8 lg:grid-cols-12 lg:gap-6">
            <div className="lg:col-span-4">
              <p className="mb-3 font-label text-[12px] font-semibold uppercase tracking-[0.14em] text-accent-500 md:text-[13px]">FAQ</p>
              <h2 id="faq-heading" className="font-heading text-[28px] font-semibold leading-[1.12] text-foreground-950 md:text-[34px] lg:text-[44px]">Common questions</h2>
            </div>
            <div className="lg:col-span-8">
              <FaqList />
            </div>
          </div>
        </Container>
      </Section>
    </>
  );
}